import React, { useCallback, useMemo } from 'react'

import { faPencil } from '@fortawesome/pro-regular-svg-icons'
import { Flex, Text } from 'govwell-ui'
import { Modal } from 'govwell-ui'
import { AIAssistantChatFragment } from 'types/graphql'

import { useNotification } from 'src/components/shared/Notification/NotificationContext'
import { useCreateAIChatbotFAQMutation } from 'src/fetch/aiChatbotFAQ'
import { useAppForm } from 'src/hooks/use-app-form'
import useDisclosure from 'src/hooks/use-disclosure'
import LinkArea from 'src/pages/AIAssistantHistoryPage/LinkArea'
import { RefetchFnType } from 'src/types'

type Props = {
  aiChat: AIAssistantChatFragment
  refetch: RefetchFnType
  children: React.ReactNode
}

const CreateCustomAnswerButton = ({ aiChat, refetch, children }: Props) => {
  const modalState = useDisclosure()
  const { mutateAsync: createAIChatbotFAQ } = useCreateAIChatbotFAQMutation()

  const initialValues = useMemo(
    () => ({
      question: aiChat.question ?? '',
      answer: aiChat.response ?? '',
    }),
    [aiChat]
  )
  const form = useAppForm({
    defaultValues: initialValues,
  })

  const { notification } = useNotification()

  const handleClick = useCallback(async () => {
    const answer = form.getFieldValue('answer')
    const question = form.getFieldValue('question')

    await createAIChatbotFAQ({
      input: {
        answer,
        question,
        createdFromChatId: aiChat.id,
      },
    })
    notification.info({
      message: `This response has been added to your Community Assistant's Custom Answers.`,
      placement: 'bottomRight',
    })
    await refetch()
    modalState.close()
  }, [createAIChatbotFAQ, form, notification, refetch, modalState, aiChat.id])

  return (
    <>
      <LinkArea cta="Create custom answer" highlightMode="hover" onClick={modalState.open}>
        {children}
      </LinkArea>
      {modalState.isOpen && (
        <Modal onClose={modalState.close} width="50%">
          <Modal.Header icon={faPencil}>Adjust Community Assistant Response</Modal.Header>
          <Modal.Content>
            <Flex direction="column" gap={6} align="start">
              <Text.Body>Create a custom answer from this Community Assistant response.</Text.Body>
              <form.AppField name="question">
                {(field) => (
                  <field.TextArea
                    caption="Edit the question for legibility, if needed."
                    field={field}
                    isRequired
                    label="Question"
                    minRows={3}
                    maxRows={5}
                    placeholder="Enter the question"
                  />
                )}
              </form.AppField>
              <form.AppField name="answer">
                {(field) => (
                  <field.RichTextInput
                    caption="Adjust the answer to make it more accurate and helpful."
                    field={field}
                    label="Answer"
                    isRequired
                  />
                )}
              </form.AppField>
            </Flex>
          </Modal.Content>
          <Modal.Footer confirmText="Create Custom Answer" onConfirm={handleClick} />
        </Modal>
      )}
    </>
  )
}

export default CreateCustomAnswerButton
